/* eslint-disable @typescript-eslint/member-ordering */
import { Inject, Injectable } from "@angular/core";
import { MatLegacyDialog } from "@angular/material/legacy-dialog";
import { Router } from "@angular/router";
import { Configuration } from "@content-platform/web-angular-shared-util-configuration";
import { DebugFlagsService } from "@content-platform/web-angular-shared-util-development";
import { LoggerService } from "@content-platform/web-angular-shared-util-logging";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { select, Store } from "@ngrx/store";
import { OktaAuth } from "@okta/okta-auth-js";
import { OKTA_AUTH } from "@okta/okta-angular";
import { debounce, debounceTime, delay, filter, map, of, switchMap, take, takeUntil, tap, timer as observableTimer, withLatestFrom } from "rxjs";
import { AuthDebugFlagNames } from "../debug-flags";
import { MSALService } from "../msal.service";
import * as authActions from "./auth.actions";
import * as authSelectors from "./auth.selectors";
import { AutoLogoutComponent } from "./auto-logout-confirmation/auto-logout.component";
import * as i0 from "@angular/core";
import * as i1 from "@angular/router";
import * as i2 from "@ngrx/effects";
import * as i3 from "../msal.service";
import * as i4 from "@content-platform/web-angular-shared-util-logging";
import * as i5 from "@content-platform/web-angular-shared-util-development";
import * as i6 from "@ngrx/store";
import * as i7 from "@angular/material/legacy-dialog";
const TIME_INTERVAL = 1000; // Time interval per second
const APPLICATION_TIMEOUT_DIALOG_TIME = TIME_INTERVAL * 60 * 5; // 5 minutes of popup to refresh the session
let APPLICATION_TIMEOUT_TIME = TIME_INTERVAL * 3600 * 24; // 24 hrs timeout
export const AUTO_LOGOUT_START_TIME = "AUTO_LOGOUT_START_TIME"; // Local storage key for auto logout start time
/**
 * Enum to keep a track of skipping timer and do some actions
 *
 * @enum {number}
 */
var AutoLogoutActions;
(function (AutoLogoutActions) {
    AutoLogoutActions[AutoLogoutActions["SHOW_LOGOUT_TIMER_DIALOG"] = APPLICATION_TIMEOUT_TIME / TIME_INTERVAL] = "SHOW_LOGOUT_TIMER_DIALOG";
    AutoLogoutActions[AutoLogoutActions["SESSION_EXPIRED_LOGOUT"] = -2] = "SESSION_EXPIRED_LOGOUT";
    AutoLogoutActions[AutoLogoutActions["SESSION_UPDATED"] = -1] = "SESSION_UPDATED";
})(AutoLogoutActions || (AutoLogoutActions = {}));
/**
 * The Auth effects imported in {@link AuthModule}
 *
 * @export
 * @class AuthEffects
 */
export class AuthEffects {
    constructor(router, actions, msalService, 
    // TODO: Optional because not all apps need it right now
    // Injecting actual service to avoid cyclic dependency
    oktaService, loggerService, debugFlags, store, dialog) {
        this.router = router;
        this.actions = actions;
        this.msalService = msalService;
        this.oktaService = oktaService;
        this.debugFlags = debugFlags;
        this.store = store;
        this.dialog = dialog;
        /**
         * Triggers the adal service login functions.
         *
         * @returns Empty resolved observable
         */
        this.login = createEffect(() => this.actions.pipe(ofType(authActions.LOGIN), tap(() => {
            this.msalService.login();
        })), { dispatch: false });
        /**
         * Sets the adal user, gets the adal user from the adal [service]{@link AdalService}
         *
         * @returns LoginSuccess action if user exists, otherwise LoginFailed action
         */
        this.setADUser = createEffect(() => this.actions.pipe(ofType(authActions.SET_AD_USER), switchMap(() => Configuration.getAsync("adal").pipe(filter((config) => !!config), map(() => {
            const user = this.msalService.getUserAccount();
            if (user) {
                if (this.debugFlags.hasDebugFlag(AuthDebugFlagNames.Impersonate)) {
                    user.profile = {
                        ...(this.debugFlags.getDebugFlagMetadata(AuthDebugFlagNames.Impersonate) || {})
                    };
                    user.profile.oid = user.profile.objectId;
                }
                return new authActions.LoginSuccess(user);
            }
            if (!user && sessionStorage.getItem("adal.error") === "access_denied") {
                const route = this.router.config.find((r) => r.path === "access-denied");
                route.data = {
                    title: "Sorry! Access Denied",
                    icon: "lock",
                    description: `You do not currently have permission to access this site. You can request access by emailing
                <a href="mailto:prodapps@rt.mtvi.com?Subject=Content%20Platform%20Application%20Suite%20Access%20Request"
                target="_top">prodapps@rt.mtvi.com</a>`
                };
                this.router.navigate(["/access-denied"], { skipLocationChange: true });
                return new authActions.LoginFailed("Sorry, user not found!");
            }
            else {
                return new authActions.Login();
            }
        })))));
        /**
         * Triggers the adal service logout function.
         *
         * @returns Logout Success action
         */
        this.logout = createEffect(() => this.actions.pipe(ofType(authActions.LOGOUT), map((action) => {
            console.log("🚀🚀 -- LOGOUT ACTION TRIGGERED --", action);
            localStorage.removeItem(AUTO_LOGOUT_START_TIME);
            this.oktaService?.signOut();
            this.oktaService?.closeSession();
            return new authActions.LogoutSuccess();
        })));
        /**
         * Listens to the LOGOUT_SUCCESS action for logs
         */
        this.logoutSuccess = createEffect(() => this.actions.pipe(ofType(authActions.LOGOUT_SUCCESS), tap(() => {
            this.removeOktaTokenFromLocalStorage();
            this.logger.log("LOGOUT SUCCESS");
        })), { dispatch: false });
        /**
         * This effect keeps a track of:
         * 1. user wants to extend the time by performing a ngrx action on the application
         * 2. user does not do any action for APPLICATION_TIMEOUT_TIME we show a dialog which runs for APPLICATION_TIMEOUT_DIALOG_TIME
         * 3. user does not do any action for APPLICATION_TIMEOUT_TIME + APPLICATION_TIMEOUT_DIALOG_TIME the system automatically logs out.
         * 4. also keeps a sync for multiple sessions with all the above 3 points mentioned
         *
         * @memberof AuthEffects
         */
        this.extendApplicationTimeout = createEffect(() => this.actions.pipe(withLatestFrom(this.store.pipe(select(authSelectors.getIsLoggedIn))), filter(([action, loggedIn]) => loggedIn && action.type !== authActions.SHOW_LOGOUT_TIMER_DIALOG), debounceTime(1000), switchMap(([action]) => {
            let currentTime = new Date().toISOString();
            // Setting the timestamp in localstorage when the timer starts or resets by any action (Syncing multiple windows)
            if (action.type !== authActions.SESSION_UPDATED && action.type !== authActions.LOGOUT) {
                localStorage.setItem(AUTO_LOGOUT_START_TIME, currentTime);
            }
            // TODO: don't trigger this every second... and remove the user of localStorage if possible.
            return observableTimer(0, TIME_INTERVAL).pipe(takeUntil(observableTimer(APPLICATION_TIMEOUT_TIME + APPLICATION_TIMEOUT_DIALOG_TIME)), map((value) => {
                const startTime = localStorage.getItem(AUTO_LOGOUT_START_TIME);
                // Logout happened already from another session
                if (!startTime) {
                    return AutoLogoutActions.SESSION_EXPIRED_LOGOUT;
                }
                // Comparing the timestamps based of the current session with the local storage session
                const dateFromLocalStorage = new Date(startTime);
                const currentDate = new Date(currentTime);
                if (dateFromLocalStorage > currentDate) {
                    currentTime = dateFromLocalStorage.toISOString();
                    // We do a debounce to skip the current timer and reinitiate the timer again
                    return AutoLogoutActions.SESSION_UPDATED;
                }
                return value + 1;
            }), debounce((value) => of({}).pipe(delay(this.getDebounceTime(value)))));
        }), map((value) => {
            // This condition triggers if there is a new time stamp initiated by another instance(session)
            switch (value) {
                case AutoLogoutActions.SHOW_LOGOUT_TIMER_DIALOG:
                    return new authActions.ShowLogoutTimerDialog();
                case AutoLogoutActions.SESSION_UPDATED:
                    // We close the dialog if the session gets extended by another instance
                    if (this.dialogRef) {
                        this.dialogRef.close();
                    }
                    return new authActions.SessionUpdated();
                default:
                    // Logging out from the app after the entire session timesout
                    return new authActions.Logout();
            }
        })));
        /**
         * This effect shows the autologout timer dialog incase user does not do any operation for
         * APPLICATION_TIMEOUT_TIME time.
         *
         * @memberof AuthEffects
         */
        this.showLogoutTimerDialog = createEffect(() => this.actions.pipe(ofType(authActions.SHOW_LOGOUT_TIMER_DIALOG), tap(() => {
            this.dialogRef = this.dialog.open(AutoLogoutComponent, {
                width: "380px",
                data: { timeout: APPLICATION_TIMEOUT_DIALOG_TIME },
                position: { top: "0%" },
                disableClose: true
            });
        })), { dispatch: false });
        this.logger = loggerService.instance("Auth");
        // Todo: For Testing purposes only, Will remove this code once testing is completed
        this.debugFlags
            .hasDebugFlagAsync(AuthDebugFlagNames.Auto_Logout_Timeout)
            .pipe(take(1))
            .subscribe((value) => {
            if (value) {
                const autoLogoutFlag = parseInt(this.debugFlags.getDebugFlagMetadata(AuthDebugFlagNames.Auto_Logout_Timeout), 0);
                if (!isNaN(Number(autoLogoutFlag))) {
                    APPLICATION_TIMEOUT_TIME = autoLogoutFlag;
                    // Not a good way but just meant for testing so thought can get around.
                    AutoLogoutActions.SHOW_LOGOUT_TIMER_DIALOG =
                        APPLICATION_TIMEOUT_TIME / TIME_INTERVAL;
                }
            }
        });
    }
    removeOktaTokenFromLocalStorage() {
        // localStorage.removeItem('okta-shared-transaction-storage');
        localStorage.removeItem("okta-token-storage");
        localStorage.removeItem("okta-cache-storage");
        // localStorage.removeItem('okta-original-uri-storage');
    }
    /**
     * This function returns the debounce delay time
     *
     * @private
     * @param {number} value
     * @returns {number}
     * @memberof AuthEffects
     */
    getDebounceTime(value) {
        switch (value) {
            case AutoLogoutActions.SESSION_EXPIRED_LOGOUT:
            case AutoLogoutActions.SESSION_UPDATED:
            case AutoLogoutActions.SHOW_LOGOUT_TIMER_DIALOG:
                return 0;
            default:
                const timeRemaining = ((APPLICATION_TIMEOUT_TIME + APPLICATION_TIMEOUT_DIALOG_TIME) / TIME_INTERVAL - value) *
                    TIME_INTERVAL;
                return timeRemaining;
        }
    }
    static { this.ɵfac = function AuthEffects_Factory(__ngFactoryType__) { return new (__ngFactoryType__ || AuthEffects)(i0.ɵɵinject(i1.Router), i0.ɵɵinject(i2.Actions), i0.ɵɵinject(i3.MSALService), i0.ɵɵinject(OKTA_AUTH), i0.ɵɵinject(i4.LoggerService), i0.ɵɵinject(i5.DebugFlagsService), i0.ɵɵinject(i6.Store), i0.ɵɵinject(i7.MatLegacyDialog)); }; }
    static { this.ɵprov = /*@__PURE__*/ i0.ɵɵdefineInjectable({ token: AuthEffects, factory: AuthEffects.ɵfac }); }
}
//# sourceMappingURL=auth.effects.js.map